import {
  ccGetCommitments,
  ccGetSnapshots,
  ccGetAccountsConfig,
  ccPatchOccurrence,
} from '../api-client';
import type { CcOccurrenceWithCommitment, CcSnapshot, CcAccountConfigRow } from '../api-client';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fmtAmount(amount: number, currency: string): string {
  return `${escapeHtml(currency)} ${Math.round(amount).toLocaleString('en-US')}`;
}

function snapshotAge(asOf: string): string {
  const diffMs = Date.now() - new Date(asOf).getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function openDrill(kind: 'cash' | 'commitments' | 'income', horizon: number, onDone: () => void): void {
  const titles: Record<typeof kind, string> = {
    cash: 'Cash available',
    commitments: 'Commitments due',
    income: 'Confirmed income',
  };

  const overlay = document.createElement('div');
  document.body.appendChild(overlay);

  overlay.innerHTML = `
    <div class="sheet-backdrop" id="drill-backdrop">
      <div class="sheet drill-sheet" role="dialog" aria-modal="true">
        <header class="sheet-header">
          <h3>${escapeHtml(titles[kind])}</h3>
          <button class="ghost sheet-close" id="drill-close" type="button" aria-label="Close">✕</button>
        </header>
        <div class="sheet-body" id="drill-body">
          <div class="sk sk-block">Loading…</div>
        </div>
      </div>
    </div>
  `;

  const close = () => {
    overlay.remove();
  };

  overlay.querySelector<HTMLElement>('#drill-backdrop')!.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) close();
  });
  overlay.querySelector<HTMLButtonElement>('#drill-close')!.addEventListener('click', close);

  void loadDrill(kind, horizon, overlay, close, onDone);
}

async function loadDrill(
  kind: 'cash' | 'commitments' | 'income',
  horizon: number,
  overlay: HTMLElement,
  close: () => void,
  onDone: () => void,
): Promise<void> {
  const body = overlay.querySelector<HTMLElement>('#drill-body')!;
  try {
    if (kind === 'cash') {
      await renderCash(body);
    } else if (kind === 'commitments') {
      await renderCommitments(body, horizon, overlay, close, onDone);
    } else {
      await renderIncome(body, horizon);
    }
  } catch (e) {
    body.innerHTML = `<div class="cc-error">Couldn't load data. <button class="ghost" id="drill-retry">Retry</button></div>`;
    body.querySelector<HTMLButtonElement>('#drill-retry')?.addEventListener('click', () => {
      body.innerHTML = '<div class="sk sk-block">Loading…</div>';
      void loadDrill(kind, horizon, overlay, close, onDone);
    });
  }
}

// ── Cash ────────────────────────────────────────────────────────────────────

async function renderCash(body: HTMLElement): Promise<void> {
  const [{ accounts }, { snapshots }] = await Promise.all([
    ccGetAccountsConfig(),
    ccGetSnapshots(),
  ]);

  const cashAccounts = accounts
    .filter((a) => a.cash_relevant)
    .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));

  const latestSnapshot = new Map<string, CcSnapshot>();
  for (const s of snapshots) {
    const existing = latestSnapshot.get(s.account_id);
    if (!existing || s.as_of > existing.as_of) {
      latestSnapshot.set(s.account_id, s);
    }
  }

  if (cashAccounts.length === 0) {
    body.innerHTML = '<div class="cc-empty">No cash-relevant accounts configured.</div>';
    return;
  }

  body.innerHTML = cashAccounts.map((a: CcAccountConfigRow) => {
    const snap = latestSnapshot.get(a.account_id);
    const snapLabel = snap
      ? `${escapeHtml(snap.currency)} ${snap.balance_native.toLocaleString('en-US')} · ${snapshotAge(snap.as_of)}`
      : 'No snapshot';
    return `
      <div class="drill-row">
        <div class="drill-row-name">${escapeHtml(a.name)}</div>
        <div class="drill-row-meta">${snapLabel}</div>
      </div>
    `;
  }).join('');
}

// ── Commitments ──────────────────────────────────────────────────────────────

async function renderCommitments(
  body: HTMLElement,
  horizon: number,
  overlay: HTMLElement,
  close: () => void,
  onDone: () => void,
): Promise<void> {
  const { occurrences } = await ccGetCommitments(horizon);
  const outOccs = occurrences.filter((o) => o.direction === 'out');

  if (outOccs.length === 0) {
    body.innerHTML = `<div class="cc-empty">No commitments due in the next ${horizon} days.</div>`;
    return;
  }

  // Track statuses locally so we can update without a full reload
  const statusMap = new Map<string, 'planned' | 'paid' | 'skipped'>(
    outOccs.map((o) => [o.id, o.status]),
  );
  // Track nudge visibility
  const nudgeMap = new Map<string, string>(); // id → account name (or generic)

  const render = () => {
    const pending = outOccs.filter((o) => statusMap.get(o.id) !== 'paid');
    const paid = outOccs.filter((o) => statusMap.get(o.id) === 'paid');

    const rowHtml = (o: CcOccurrenceWithCommitment) => {
      const status = statusMap.get(o.id) ?? o.status;
      const isPaid = status === 'paid';
      const nudge = nudgeMap.get(o.id);
      const dateLabel = escapeHtml(o.due_date.slice(5)); // MM-DD
      const amtLabel = fmtAmount(o.amount_native, o.currency);
      return `
        <div class="drill-row drill-occ${isPaid ? ' drill-occ-paid' : ''}" data-occ-id="${escapeHtml(o.id)}">
          <div class="drill-row-top">
            <div class="drill-row-name">${escapeHtml(o.name)}</div>
            <div class="drill-row-meta">${amtLabel} · ${dateLabel}</div>
          </div>
          ${nudge ? `<div class="drill-nudge">Mark <strong>${escapeHtml(nudge)}</strong> paid — update the paying account balance?</div>` : ''}
          ${!isPaid ? `
            <div class="drill-occ-actions">
              <button class="ghost drill-action" data-action="mark-paid" data-occ-id="${escapeHtml(o.id)}" type="button">Mark paid</button>
              <button class="ghost drill-action" data-action="mark-skipped" data-occ-id="${escapeHtml(o.id)}" type="button">Skip</button>
            </div>
          ` : ''}
        </div>
      `;
    };

    body.innerHTML = [
      ...pending.map(rowHtml),
      paid.length > 0 ? '<div class="drill-section-label">Paid</div>' : '',
      ...paid.map(rowHtml),
    ].join('');

    body.querySelectorAll<HTMLButtonElement>('[data-action="mark-paid"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-occ-id')!;
        const occ = outOccs.find((o) => o.id === id);
        if (!occ) return;
        btn.disabled = true;
        try {
          await ccPatchOccurrence(id, 'paid');
          statusMap.set(id, 'paid');
          // Show nudge with account name or commitment name
          nudgeMap.set(id, occ.name);
          render();
          onDone();
        } catch {
          btn.disabled = false;
        }
      });
    });

    body.querySelectorAll<HTMLButtonElement>('[data-action="mark-skipped"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-occ-id')!;
        btn.disabled = true;
        try {
          await ccPatchOccurrence(id, 'skipped');
          statusMap.set(id, 'skipped');
          nudgeMap.delete(id);
          render();
          onDone();
        } catch {
          btn.disabled = false;
        }
      });
    });
  };

  render();
}

// ── Income ───────────────────────────────────────────────────────────────────

async function renderIncome(body: HTMLElement, horizon: number): Promise<void> {
  const { occurrences } = await ccGetCommitments(horizon);
  const inOccs = occurrences.filter((o) => o.direction === 'in');

  if (inOccs.length === 0) {
    body.innerHTML = `<div class="cc-empty">No income due in the next ${horizon} days.</div>`;
    return;
  }

  const confirmed = inOccs.filter((o) => o.confirmed === 1);
  const unconfirmed = inOccs.filter((o) => o.confirmed !== 1);

  const rowHtml = (o: CcOccurrenceWithCommitment) => {
    const dateLabel = escapeHtml(o.due_date.slice(5));
    const amtLabel = fmtAmount(o.amount_native, o.currency);
    return `
      <div class="drill-row">
        <div class="drill-row-name">${escapeHtml(o.name)}</div>
        <div class="drill-row-meta">${amtLabel} · ${dateLabel}</div>
      </div>
    `;
  };

  const sections: string[] = [];

  if (confirmed.length > 0) {
    sections.push('<div class="drill-section-label">Confirmed</div>');
    sections.push(...confirmed.map(rowHtml));
  }

  if (unconfirmed.length > 0) {
    sections.push('<div class="drill-section-label">Unconfirmed</div>');
    sections.push(...unconfirmed.map(rowHtml));
  }

  body.innerHTML = sections.join('');
}
